import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Container, Row, Col, Card, Button, Form, Modal, Badge } from "react-bootstrap";
import {
  BsSearch,
  BsZoomIn,
  BsChevronLeft,
  BsChevronRight,
  BsStarFill,
  BsCalendar3,
  BsTag,
  BsX,
  BsLink45Deg,
  BsBoxArrowUpRight
} from "react-icons/bs";
import { SERVER_PATH } from "../../config/env";
import "./gallery.css";

const CATEGORIES = [
  "All",
  "Farm",
  "Products",
  "Harvest",
  "Certificates",
  "Awards",
  "Customers",
  "Events",
  "Team",
  "Herbal Plants",
  "Others"
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Lightbox Modal State
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Set SEO Meta Tags on Component Mount
  useEffect(() => {
    document.title = "Our Gallery - Sivam Farms";

    // Set or Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = "Explore Sivam Farms organic cultivation process, products, farm events, awards, certificates, and nature gallery.";

    // OpenGraph Title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = "Our Gallery - Sivam Farms";

    window.scrollTo(0, 0);
  }, []);

  const fetchGalleryData = useCallback((cat, search, pageNum, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    axios
      .get(`${SERVER_PATH}get/gallery.php`, {
        params: {
          category: cat === "All" ? "all" : cat,
          status: "Active",
          search: search,
          page: pageNum,
          limit: 12,
        },
      })
      .then((res) => {
        if (res.data) {
          const newItems = res.data.gallery || [];
          if (append) {
            setItems((prev) => [...prev, ...newItems]);
          } else {
            setItems(newItems);
          }
          setTotalPages(res.data.total_pages || 1);
          setTotalCount(res.data.total || 0);
        }
        setLoading(false);
        setLoadingMore(false);
      })
      .catch((err) => {
        console.log("Gallery fetch error:", err);
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  // Effect on Filter or Search Change
  useEffect(() => {
    setPage(1);
    fetchGalleryData(selectedCategory, searchQuery, 1, false);
  }, [selectedCategory, searchQuery, fetchGalleryData]);

  const handleLoadMore = () => {
    if (page < totalPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchGalleryData(selectedCategory, searchQuery, nextPage, true);
    }
  };

  // Navigation Link Click Handler
  const handleNavigateLink = (e, rawUrl) => {
    e.stopPropagation();
    if (!rawUrl) return;
    const url = rawUrl.trim();
    if (url.startsWith("/")) {
      window.location.href = url;
    } else if (url.startsWith("http://") || url.startsWith("https://")) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.open(`https://${url}`, "_blank", "noopener,noreferrer");
    }
  };

  // Lightbox Navigation Functions
  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
  };

  const handleCloseLightbox = () => {
    setLightboxIndex(null);
  };

  const handlePrev = useCallback(() => {
    if (lightboxIndex !== null) {
      setLightboxIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
    }
  }, [lightboxIndex, items.length]);

  const handleNext = useCallback(() => {
    if (lightboxIndex !== null) {
      setLightboxIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
    }
  }, [lightboxIndex, items.length]);

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") handleCloseLightbox();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, handlePrev, handleNext]);

  // Touch Swipe Handlers for Mobile Lightbox
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) handleNext(); // swipe left -> next
    if (distance < -50) handlePrev(); // swipe right -> prev
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const renderSkeletonGrid = () => (
    <Row className="g-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
        <Col key={n} lg={3} md={4} sm={6} xs={12}>
          <div className="gallery-skeleton-card shadow-xs" style={{ height: "300px" }}></div>
        </Col>
      ))}
    </Row>
  );

  const activeItem = lightboxIndex !== null ? items[lightboxIndex] : null;
  const activeFullImgUrl = activeItem
    ? activeItem.image.startsWith("http")
      ? activeItem.image
      : `${SERVER_PATH.replace("controllers/api/", "")}${activeItem.image}`
    : null;

  return (
    <div className="gallery-page-container pb-5 bg-light bg-opacity-25" style={{ minHeight: "80vh" }}>
      {/* HERO BANNER */}
      <div className="gallery-hero-banner mb-5">
        <Container>
          <h1 className="gallery-hero-title">OUR GALLERY</h1>
          <p className="gallery-hero-subtitle">
            Explore our farm, products, nature, organic cultivation process, and memorable moments.
          </p>
        </Container>
      </div>

      <Container>
        {/* FILTERS & SEARCH ROW */}
        <div className="d-flex flex-column gap-3 mb-4">
          {/* Category Filter Pills */}
          <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
            {CATEGORIES.map((cat) => (
              <span
                key={cat}
                className={`gallery-filter-pill ${selectedCategory === cat ? "active" : ""}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </span>
            ))}
          </div>

          {/* Search Input */}
          <Row className="justify-content-center mt-2">
            <Col md={6} lg={5}>
              <div className="position-relative">
                <BsSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-success fs-5" style={{ zIndex: 5 }} />
                <Form.Control
                  type="text"
                  placeholder="Search gallery by title or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="gallery-search-input py-2 ps-5 pe-3"
                />
                {searchQuery && (
                  <BsX
                    className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted fs-4 cursor-pointer"
                    onClick={() => setSearchQuery("")}
                    style={{ zIndex: 5 }}
                  />
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* GALLERY MASONRY GRID */}
        {loading ? (
          renderSkeletonGrid()
        ) : items.length === 0 ? (
          <div className="empty-gallery-state text-center py-5 px-4 bg-white rounded-4 shadow-xs my-4 border">
            <h4 className="fw-bold text-dark mb-2">No Gallery Images Available</h4>
            <p className="text-secondary mb-3">
              {searchQuery || selectedCategory !== "All"
                ? "No photos matched your filter or search query. Try clearing your filters."
                : "Check back soon for new photos from Sivam Farms."}
            </p>
            {(searchQuery || selectedCategory !== "All") && (
              <Button
                variant="success"
                className="rounded-pill px-4 fw-semibold"
                onClick={() => {
                  setSelectedCategory("All");
                  setSearchQuery("");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <Row className="g-4">
              {items.map((item, index) => {
                const fullImgUrl = item.image.startsWith("http")
                  ? item.image
                  : `${SERVER_PATH.replace("controllers/api/", "")}${item.image}`;
                const isFeatured = Number(item.featured) === 1;

                return (
                  <Col key={item.id} lg={3} md={4} sm={6} xs={12}>
                    <Card className="gallery-card h-100 shadow-xs">
                      <div
                        className="gallery-card-img-wrapper"
                        onClick={() => handleOpenLightbox(index)}
                      >
                        {isFeatured && (
                          <span className="gallery-featured-badge d-flex align-items-center gap-1">
                            <BsStarFill style={{ fontSize: "0.7rem" }} /> Featured
                          </span>
                        )}
                        <img
                          src={fullImgUrl}
                          alt={item.title}
                          className="gallery-card-img"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="gallery-card-overlay">
                          <BsZoomIn className="display-6" />
                        </div>
                      </div>

                      <Card.Body className="p-3 d-flex flex-column justify-content-between">
                        <div>
                          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <span className="gallery-category-badge d-inline-flex align-items-center gap-1">
                              <BsTag /> {item.category}
                            </span>
                            <small className="text-muted small d-inline-flex align-items-center gap-1">
                              <BsCalendar3 />
                              {new Date(item.created_at).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </small>
                          </div>
                          <h6 className="fw-bold text-dark mb-1 text-truncate" title={item.title}>
                            {item.title}
                          </h6>
                          <p className="text-secondary small mb-0 line-clamp-2" style={{ maxHeight: "40px", overflow: "hidden" }}>
                            {item.description}
                          </p>

                          {(() => {
                            let parsedLinks = [];
                            if (item.links) {
                              try {
                                parsedLinks = typeof item.links === "string" ? JSON.parse(item.links) : item.links;
                              } catch (_) {}
                            }
                            if (!Array.isArray(parsedLinks)) return null;
                            const validLinks = parsedLinks.filter((l) => l && l.url);
                            if (validLinks.length === 0) return null;

                            return (
                              <div className="d-flex flex-wrap gap-1 mt-2 pt-2 border-top">
                                {validLinks.map((linkObj, lIdx) => (
                                  <Button
                                    key={lIdx}
                                    variant="outline-success"
                                    size="sm"
                                    className="rounded-pill py-1 px-3 small fw-semibold d-inline-flex align-items-center gap-1"
                                    onClick={(e) => handleNavigateLink(e, linkObj.url)}
                                    title={linkObj.url}
                                  >
                                    <BsLink45Deg />
                                    {linkObj.label || "Visit Link"}
                                    <BsBoxArrowUpRight style={{ fontSize: "0.75rem" }} />
                                  </Button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* LOAD MORE BUTTON */}
            {page < totalPages && (
              <div className="text-center mt-5">
                <Button
                  variant="success"
                  size="lg"
                  className="btn-load-more rounded-pill px-5 py-2 fw-semibold shadow-sm"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading More..." : "Load More Photos"}
                </Button>
              </div>
            )}
          </>
        )}
      </Container>

      {/* FULLSCREEN LIGHTBOX MODAL */}
      <Modal
        show={lightboxIndex !== null}
        onHide={handleCloseLightbox}
        centered
        size="xl"
        className="gallery-lightbox-modal"
      >
        <Modal.Body className="p-3 position-relative">
          {/* Close Button */}
          <button
            type="button"
            className="btn-close btn-close-white position-absolute top-0 end-0 m-3 z-3"
            onClick={handleCloseLightbox}
            aria-label="Close"
          ></button>

          {activeItem && (
            <Row className="g-0 align-items-center" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
              {/* Image Preview Side */}
              <Col lg={8} className="text-center position-relative py-3">
                {/* Navigation Buttons */}
                {items.length > 1 && (
                  <>
                    <button
                      className="gallery-lightbox-nav-btn position-absolute top-50 start-0 translate-middle-y ms-2 z-2"
                      onClick={handlePrev}
                      title="Previous Image (Left Arrow)"
                    >
                      <BsChevronLeft />
                    </button>
                    <button
                      className="gallery-lightbox-nav-btn position-absolute top-50 end-0 translate-middle-y me-2 z-2"
                      onClick={handleNext}
                      title="Next Image (Right Arrow)"
                    >
                      <BsChevronRight />
                    </button>
                  </>
                )}

                <img
                  src={activeFullImgUrl}
                  alt={activeItem.title}
                  className="img-fluid rounded shadow-lg"
                  decoding="async"
                  style={{ maxHeight: "75vh", objectFit: "contain" }}
                />
              </Col>

              {/* Information Side */}
              <Col lg={4} className="p-4 text-light">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <Badge bg="success" className="px-3 py-2 rounded-pill fs-6 fw-normal">
                    {activeItem.category}
                  </Badge>
                  <small className="text-light opacity-75">
                    {lightboxIndex + 1} of {items.length}
                  </small>
                </div>

                <h4 className="fw-bold mb-3 text-white">{activeItem.title}</h4>
                <p className="text-light opacity-90 leading-relaxed mb-4" style={{ whiteSpace: "pre-line", maxHeight: "250px", overflowY: "auto" }}>
                  {activeItem.description}
                </p>

                {(() => {
                  let parsedLinks = [];
                  if (activeItem.links) {
                    try {
                      parsedLinks = typeof activeItem.links === "string" ? JSON.parse(activeItem.links) : activeItem.links;
                    } catch (_) {}
                  }
                  if (!Array.isArray(parsedLinks)) return null;
                  const validLinks = parsedLinks.filter((l) => l && l.url);
                  if (validLinks.length === 0) return null;

                  return (
                    <div className="mb-4">
                      <small className="text-light opacity-75 d-block mb-2 fw-semibold text-uppercase" style={{ letterSpacing: "0.5px" }}>
                        Associated Links:
                      </small>
                      <div className="d-flex flex-wrap gap-2">
                        {validLinks.map((linkObj, lIdx) => (
                          <Button
                            key={lIdx}
                            variant="success"
                            size="sm"
                            className="rounded-pill py-1 px-3 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                            onClick={(e) => handleNavigateLink(e, linkObj.url)}
                          >
                            <BsLink45Deg />
                            {linkObj.label || "Visit Link"}
                            <BsBoxArrowUpRight style={{ fontSize: "0.75rem" }} />
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="pt-3 border-top border-secondary border-opacity-50 text-light opacity-75 small d-flex align-items-center gap-2">
                  <BsCalendar3 />
                  <span>
                    Uploaded on{" "}
                    {new Date(activeItem.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
